<template>
	<b-table
		:data="chainsList"
		:striped="true"
		:loading="loading"
		:paginated="showPagination"
		:per-page="rowsPerPage"
		pagination-position="both"
		default-sort="chainName"
		default-sort-direction="asc"
		height=65vh
		focusable
		scrollable
		sticky-header
	>
		<b-table-column
			v-slot="props"
			sortable
			field="chainId"
			:label="$t('ChainId')"
			centered
		>
			{{ props.row.chainId }}
		</b-table-column>
		<b-table-column
			v-slot="props"
			sortable
			field="chainName"
			:label="$t('ChainName')"
			centered
		>
			{{ props.row.chainName }}
		</b-table-column>
		<b-table-column
			v-slot="props"
			sortable
			field="shopsRelated"
			:label="$t('Shops')"
			:visible="showRelatedItems"
			centered
		>
			<b-button 
			type="is-text"
			@click="showRelatedShops(props.row.chainId)"
			data-test-id="chainShopsCount"
			>
				{{ props.row.shopsRelated?.length ?? 0 }}
			</b-button>
		</b-table-column>
		<b-table-column
			v-slot="props"
			sortable
			field="groupsRelated"
			:label="$t('NotificationGroups')"
			:visible="showRelatedItems"
			centered
		>
			<b-button
				type="is-text"
				@click="showRelatedGroups(props.row.chainId)"
				data-test-id="chainGroupsCount"
			>
				{{ props.row.groupsRelated?.length ?? 0 }}
			</b-button>
		</b-table-column>
		<b-table-column
			v-slot="props"
			:label="$t('Edit')"
			:visible="canEdit"
			centered
		>
			<b-button
				type="is-warning"
				icon-pack="fas"
				icon-right="pencil-alt"
				tag="router-link"
				:to="{
					name: `ChainAdministrationEdit`,
					params: { id: props.row.chainId },
					query: { returnTo: $route.path },
				}"
			/>
		</b-table-column>
		<template slot="top-left">
			<b-field :label="$t('ShopsPerPage')" type="is-blue">
				<b-select v-model="rowsPerPage" expanded @input="setPageSize">
					<option value="10">10</option>
					<option value="25">25</option>
					<option value="50">50</option>
					<option value="75">75</option>
					<option value="100">100</option>
				</b-select>
			</b-field>
		</template>
		<template slot="empty">
			<div v-if="loading" class="content has-text-grey has-text-centered">
				<p>{{ $t("Loading") }}</p>
			</div>
			<div v-else class="content has-text-grey has-text-centered">
				<p><b-icon pack="fas" icon="frown-open" size="is-large" /></p>
				<p>{{ $t("NoDataToShow") }}</p>
			</div>
		</template>
	</b-table>
</template>
<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import localStorageAccess from '@/resourceAccess/localStorageAccess'
import type { Chain } from '@/stores/app'

interface Props {
	chainsList: Chain[]
	loading: boolean
	showRelatedItems: boolean
	canEdit: boolean
}

interface Emits {
	(e: 'shop-count-clicked', chainId: number): void
	(e: 'notification-group-count-clicked', chainId: number): void
}

const props = withDefaults(defineProps<Props>(), {
	chainsList: () => [],
	loading: false,
	showRelatedItems: false,
	canEdit: false,
})

const emit = defineEmits<Emits>()

const rowsPerPage = ref<number>(10)

const showPagination = computed(() => {
	return props.chainsList ? props.chainsList.length > 10 : false
})

onMounted(() => {
	const savedRowsPerPage = localStorageAccess.getRowsPerPage('chainsList')
	if (savedRowsPerPage) {
		rowsPerPage.value = parseInt(savedRowsPerPage, 10)
	}
})

function setPageSize(): void {
	localStorageAccess.saveRowsPerPage('chainsList', rowsPerPage.value)
}

function showRelatedShops(chainId: number): void {
	emit('shop-count-clicked', chainId)
}

function showRelatedGroups(chainId: number): void {
	emit('notification-group-count-clicked', chainId)
}
</script>
